import React, { useState, useEffect } from 'react'
import { Button, Card, Checkbox, DatePicker, Form, Icon, Input, InputNumber, message, Select, Spin, Upload } from 'antd'
import moment from 'moment'
import AssetSearch from '../../components/AssetSearch'
import MasterModal from '../../components/MasterModal'
import LocationPickerModal from '../../components/LocationPickerModal'
import { sanitizeFieldName } from '../../util/methods'

const { Option } = Select
const { TextArea } = Input

export const TRIP_TYPES = ['DELIVERY', 'PICKUP', 'PASSENGER', 'TOWING', 'TRANSFER', 'SERVICE', 'OTHER']
export const TRIP_STATUSES = ['DRAFT', 'PLANNED', 'ASSIGNED', 'STARTED', 'PAUSED', 'COMPLETED', 'CANCELLED']

const prettify = (v) => (v ? v.charAt(0) + v.slice(1).toLowerCase() : v)

const SECTION_TITLE_COLOR = 'rgba(0, 0, 0, 0.85)'

// Sectioned container with a transparent header bar and a visible title.
// Uses an antd inner Card so the body still adapts to the light / dark theme.
function Section ({ title, children, style }) {
  return (
    <Card
      type='inner'
      title={<span style={{ color: SECTION_TITLE_COLOR, fontWeight: 600, letterSpacing: 0.5 }}>{title}</span>}
      headStyle={{ background: 'transparent', minHeight: 40 }}
      bodyStyle={{ padding: 16 }}
      style={{ marginBottom: 16, ...(style || {}) }}
    >
      {children}
    </Card>
  )
}

// Renders a lat / lng / address group using flat form field names, assembled into a geo object on submit.
// Latitude / Longitude sit side by side, Address spans full width, and a full-width
// "Select on Map" button opens a map picker and writes back into the form fields.
function GeoFields ({ form, getFieldDecorator, prefix, initial }) {
  const [pickerVisible, setPickerVisible] = React.useState(false)

  const handlePicked = ({ latitude, longitude, address }) => {
    form.setFieldsValue({
      [`${prefix}Lat`]: latitude,
      [`${prefix}Lng`]: longitude,
      [`${prefix}Address`]: address || form.getFieldValue(`${prefix}Address`)
    })
    setPickerVisible(false)
  }

  return (
    <React.Fragment>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 120px', minWidth: 0 }}>
          <Form.Item label='Latitude'>
            {getFieldDecorator(`${prefix}Lat`, { initialValue: initial && initial.latitude != null ? initial.latitude : undefined })(
              <InputNumber style={{ width: '100%' }} placeholder='Latitude' step={0.000001} />
            )}
          </Form.Item>
        </div>
        <div style={{ flex: '1 1 120px', minWidth: 0 }}>
          <Form.Item label='Longitude'>
            {getFieldDecorator(`${prefix}Lng`, { initialValue: initial && initial.longitude != null ? initial.longitude : undefined })(
              <InputNumber style={{ width: '100%' }} placeholder='Longitude' step={0.000001} />
            )}
          </Form.Item>
        </div>
      </div>
      <Form.Item label='Address'>
        {getFieldDecorator(`${prefix}Address`, { initialValue: initial && initial.address })(
          <Input placeholder='Address (optional)' />
        )}
      </Form.Item>
      <Button icon='environment' onClick={() => setPickerVisible(true)} style={{ width: '100%' }} title='Select on map'>Select on Map</Button>
      <LocationPickerModal
        visible={pickerVisible}
        initialLat={form.getFieldValue(`${prefix}Lat`)}
        initialLng={form.getFieldValue(`${prefix}Lng`)}
        initialAddress={form.getFieldValue(`${prefix}Address`)}
        onOk={handlePicked}
        onCancel={() => setPickerVisible(false)}
      />
    </React.Fragment>
  )
}

function Create (props) {
  const { form, match, fetch, data, title, resource, loading, filters, onSubmit, assetData, assetSearchLoading, searchGlobalAssetData, authUser } = props
  const { getFieldDecorator, validateFields } = form
  const isEdit = title === 'EDIT'

  const [asset, setAsset] = useState(null)
  const [startNow, setStartNow] = useState(false)
  const [completeNow, setCompleteNow] = useState(false)
  const [documents, setDocuments] = useState([])
  const [existingDocuments, setExistingDocuments] = useState([])

  useEffect(() => {
    if (isEdit && match && match.params && match.params.id && fetch) {
      fetch(match.params.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isEdit && data && data._id) {
      const assetId = (data.asset && data.asset._id) ? data.asset._id : data.asset
      setAsset(assetId)
      setExistingDocuments(data.documents || [])
      form.setFieldsValue({
        asset: assetId,
        tripName: data.tripName,
        description: data.description,
        tripType: data.tripType,
        manpower: (data.manpower && data.manpower._id) || data.manpower,
        remarks: data.remarks,
        proposedStartDateTime: data.proposedStartDateTime ? moment(data.proposedStartDateTime) : undefined,
        proposedEndDateTime: data.proposedEndDateTime ? moment(data.proposedEndDateTime) : undefined,
        proposedStartLat: data.proposedStartLocation && data.proposedStartLocation.latitude,
        proposedStartLng: data.proposedStartLocation && data.proposedStartLocation.longitude,
        proposedStartAddress: data.proposedStartLocation && data.proposedStartLocation.address,
        proposedEndLat: data.proposedEndLocation && data.proposedEndLocation.latitude,
        proposedEndLng: data.proposedEndLocation && data.proposedEndLocation.longitude,
        proposedEndAddress: data.proposedEndLocation && data.proposedEndLocation.address
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data && data._id])

  const manpowerFilter = (filters && filters['manpower']) || {}
  const manpowerOptions = (manpowerFilter.data && manpowerFilter.data.data) || []
  const showManpowerNewButton = !!(manpowerFilter.options && manpowerFilter.options.master)

  const buildGeo = (values, prefix) => {
    const lat = values[`${prefix}Lat`]
    const lng = values[`${prefix}Lng`]
    const address = values[`${prefix}Address`]
    if ((lat === undefined || lat === null || lat === '') &&
        (lng === undefined || lng === null || lng === '') && !address) {
      return undefined
    }
    return {
      latitude: lat === undefined || lat === '' ? null : Number(lat),
      longitude: lng === undefined || lng === '' ? null : Number(lng),
      address: address || undefined
    }
  }

  const handleStartNowChange = (e) => {
    const checked = e.target.checked
    setStartNow(checked)
    if (!checked) {
      setCompleteNow(false)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    validateFields((err, values) => {
      if (err) {
        return
      }
      if (!asset) {
        message.error('Asset is required')
        return
      }

      const payload = {
        tripName: values.tripName,
        description: values.description,
        tripType: values.tripType,
        manpower: values.manpower || undefined,
        remarks: values.remarks,
        proposedStartDateTime: values.proposedStartDateTime,
        proposedEndDateTime: values.proposedEndDateTime,
        proposedStartLocation: buildGeo(values, 'proposedStart'),
        proposedEndLocation: buildGeo(values, 'proposedEnd')
      }

      if (isEdit) {
        onSubmit({ ...payload, documents, existingDocuments }, match.params.id)
      } else {
        onSubmit({ ...payload, asset, startNow, completeNow: startNow && completeNow, documents })
      }
    })
  }

  // fileList is controlled by `documents`, so invalid files simply aren't added.
  const handleBeforeUpload = (file) => {
    const isValidType = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'].includes(file.type)
    if (!isValidType) {
      message.error(`${file.name} is not a supported file (PDF, JPG, PNG only)`)
      return false
    }
    const isWithinSize = file.size / 1024 / 1024 <= 10
    if (!isWithinSize) {
      message.error(`${file.name} exceeds the 10MB limit`)
      return false
    }
    setDocuments(prev => [...prev, file])
    return false
  }

  return (
    <Spin spinning={loading}>
      <Card className='gx-card-metrics' title={`${title} ${sanitizeFieldName(resource)}`}>
        <Form onSubmit={handleSubmit} layout='vertical'>

          <Section title='TRIP INFORMATION'>
            <Form.Item label='Trip Name'>
              {getFieldDecorator('tripName', {
                rules: [{ required: true, message: 'Trip name is required!' }]
              })(<Input placeholder='Enter trip name' />)}
            </Form.Item>

            <Form.Item label='Description'>
              {getFieldDecorator('description', {})(<TextArea rows={2} placeholder='Trip details / description' />)}
            </Form.Item>

            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                <Form.Item label='Asset'>
                  {getFieldDecorator('asset', {
                    rules: [{ required: true, message: 'Asset is required!' }]
                  })(
                    <AssetSearch
                      styleName='gx-lt-icon-search-bar-lg'
                      placeholder='Search and select an asset...'
                      onChange={searchGlobalAssetData}
                      onSelect={(value) => setAsset(value)}
                      assetData={assetData && assetData.filter(ast => ast.assetModel)}
                      authUser={authUser}
                      loading={assetSearchLoading}
                      disabled={isEdit}
                      defaultAsset={isEdit ? (data && data.asset) : undefined}
                    />
                  )}
                </Form.Item>
              </div>
              <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                <Form.Item label='Trip Type'>
                  {getFieldDecorator('tripType', {
                    rules: [{ required: true, message: 'Trip type is required!' }]
                  })(
                    <Select showSearch placeholder='Select trip type' optionFilterProp='children'>
                      {TRIP_TYPES.map(t => <Option key={t} value={t}>{prettify(t)}</Option>)}
                    </Select>
                  )}
                </Form.Item>
              </div>
            </div>

            <Form.Item label='Operator (optional)' style={{ marginBottom: 0 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  {getFieldDecorator('manpower', {})(
                    <Select showSearch allowClear placeholder='Select operator' optionFilterProp='children'>
                      {manpowerOptions.map(m => <Option key={m._id} value={m._id}>{m.name}</Option>)}
                    </Select>
                  )}
                </div>
                {showManpowerNewButton && (
                  <MasterModal resource={resource} item={manpowerFilter.options.ref} title={'CREATE'} buttonText='NEW OPERATOR' />
                )}
              </div>
            </Form.Item>
          </Section>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div style={{ flex: '1 1 320px', minWidth: 0 }}>
              <Section title='PROPOSED START'>
                <Form.Item label='Proposed Start Date & Time'>
                  {getFieldDecorator('proposedStartDateTime', {})(
                    <DatePicker showTime format='DD/MM/YYYY HH:mm' style={{ width: '100%' }} />
                  )}
                </Form.Item>
                <GeoFields form={form} getFieldDecorator={getFieldDecorator} prefix='proposedStart' initial={data && data.proposedStartLocation} />
              </Section>
            </div>
            <div style={{ flex: '1 1 320px', minWidth: 0 }}>
              <Section title='PROPOSED END'>
                <Form.Item label='Proposed End Date & Time'>
                  {getFieldDecorator('proposedEndDateTime', {})(
                    <DatePicker showTime format='DD/MM/YYYY HH:mm' style={{ width: '100%' }} />
                  )}
                </Form.Item>
                <GeoFields form={form} getFieldDecorator={getFieldDecorator} prefix='proposedEnd' initial={data && data.proposedEndLocation} />
              </Section>
            </div>
          </div>

          <Section title='ADDITIONAL DETAILS'>
            <Form.Item label='Remarks'>
              {getFieldDecorator('remarks', {})(<TextArea rows={2} placeholder='Remarks (optional)' />)}
            </Form.Item>

            <Form.Item label='Documents (optional)' style={{ width: '100%' }}>
              {isEdit && existingDocuments.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ marginBottom: 4, color: '#8c8c8c' }}>Existing documents:</div>
                  {existingDocuments.map((doc, idx) => (
                    <div key={doc.url || idx} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <a href={doc.url} target='_blank' rel='noopener noreferrer'>{doc.name || doc.url}</a>
                      <a onClick={() => setExistingDocuments(prev => prev.filter((d, i) => (d.url || i) !== (doc.url || idx)))} style={{ color: '#f5222d' }}>Remove</a>
                    </div>
                  ))}
                </div>
              )}
              <Upload.Dragger
                multiple
                fileList={documents}
                accept='.pdf,.jpg,.jpeg,.png'
                beforeUpload={handleBeforeUpload}
                onRemove={(file) => {
                  setDocuments(prev => prev.filter(f => f.uid !== file.uid))
                }}
              >
                <p className='ant-upload-drag-icon'>
                  <Icon type='file-add' />
                </p>
                <p className='ant-upload-text'>Click to upload or drag and drop</p>
                <p className='ant-upload-hint'>PDF, JPG, PNG (Max 10MB)</p>
                <Button style={{ marginTop: 8 }}>SELECT FILES</Button>
              </Upload.Dragger>
            </Form.Item>

            {!isEdit && (
              <div>
                <div>
                  <Checkbox checked={startNow} onChange={handleStartNowChange}>
                    <span style={{ fontWeight: 600 }}>Start the trip immediately</span>
                  </Checkbox>
                  <div style={{ marginLeft: 24, color: '#8c8c8c', fontSize: 12 }}>Adds a STARTED status immediately after planning.</div>
                </div>
                <div style={{ marginTop: 10 }}>
                  <Checkbox
                    checked={completeNow}
                    disabled={!startNow}
                    onChange={(e) => setCompleteNow(e.target.checked)}
                  >
                    <span style={{ fontWeight: 600 }}>Also mark the trip as completed</span>
                  </Checkbox>
                  <div style={{ marginLeft: 24, color: '#8c8c8c', fontSize: 12 }}>Adds a COMPLETED status — available only when the trip is started.</div>
                </div>
              </div>
            )}
          </Section>

          <Form.Item>
            <Button type='primary' className='gx-mb-0' htmlType='submit' loading={loading}>
              {isEdit ? 'UPDATE' : 'CREATE'}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </Spin>
  )
}

const CreateForm = Form.create({
  name: 'fleet_trip',
  onFieldsChange (props, changedFields) {
    if (props.onChange) props.onChange(changedFields)
  }
})(Create)

export default CreateForm
